import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiArrowLeft, FiBarChart2, FiTarget, FiZap, FiPlus, FiExternalLink, FiClock, FiAlertCircle } = FiIcons;

const QUESTIONS = [
  {
    id: 1,
    category: 'Visibility',
    q: "How often do you post on Instagram/Facebook?",
    options: [
      { text: "0–1x/week", score: 0 },
      { text: "2–3x/week", score: 5 },
      { text: "4–6x/week", score: 10 }
    ]
  },
  {
    id: 2,
    category: 'Visibility',
    q: "Are you consistently reaching local pet owners (not just your current followers)?",
    options: [
      { text: "Not really / I’m not sure", score: 0 },
      { text: "Somewhat", score: 5 },
      { text: "Yes—steady reach/engagement", score: 10 }
    ]
  },
  {
    id: 3,
    category: 'Trust/Proof',
    q: "Do you have clear proof on your page? (reviews, happy client pics, behind-the-scenes)",
    options: [
      { text: "Barely any", score: 0 },
      { text: "Some", score: 5 },
      { text: "Yes, it’s obvious", score: 10 }
    ]
  },
  {
    id: 4,
    category: 'Trust/Proof',
    q: "Is your profile instantly clear? (service + location + how to book)",
    options: [
      { text: "No / confusing", score: 0 },
      { text: "Mostly clear", score: 5 },
      { text: "Crystal clear", score: 10 }
    ]
  },
  {
    id: 5,
    category: 'Offer Clarity',
    q: "Is it obvious what you offer and who it’s for?",
    options: [
      { text: "No", score: 0 },
      { text: "Somewhat", score: 5 },
      { text: "Yes", score: 10 }
    ]
  },
  {
    id: 6,
    category: 'Offer Clarity',
    q: "Do you have a simple, specific offer someone can say yes to?",
    options: [
      { text: "Not really", score: 0 },
      { text: "Kind of", score: 5 },
      { text: "Yes", score: 10 }
    ]
  },
  {
    id: 7,
    category: 'Conversion Path',
    q: "How easy is it for someone to take the next step?",
    options: [
      { text: "Not sure how to contact/book", score: 0 },
      { text: "They can DM, but it’s not guided", score: 5 },
      { text: "Clear CTA + link/form + what happens next", score: 10 }
    ]
  },
  {
    id: 8,
    category: 'Conversion Path',
    q: "When someone is interested, do you have a simple booking flow?",
    options: [
      { text: "No—it's messy", score: 0 },
      { text: "Somewhat", score: 5 },
      { text: "Yes—simple steps", score: 10 }
    ]
  },
  {
    id: 9,
    category: 'Follow-Up',
    q: "How fast do you respond to inquiries most days?",
    options: [
      { text: "24+ hours", score: 0 },
      { text: "Within 4–12 hours", score: 5 },
      { text: "Within 0–3 hours", score: 10 }
    ]
  },
  {
    id: 10,
    category: 'Follow-Up',
    q: "Do you follow up if someone doesn’t reply?",
    options: [
      { text: "Never", score: 0 },
      { text: "Sometimes", score: 5 },
      { text: "Yes, consistently", score: 10 }
    ]
  }
];

export default function LeadFunnelScore() {
  const [step, setStep] = useState(0); // 0 is landing, 1-10 is questions, 11 is results
  const [answers, setAnswers] = useState({});
  const [activeFaq, setActiveFaq] = useState(null);

  const calculateResults = () => {
    const totalScore = Object.values(answers).reduce((acc, curr) => acc + curr, 0);
    
    const categories = {
      'Visibility': answers[1] + answers[2],
      'Trust/Proof': answers[3] + answers[4],
      'Offer Clarity': answers[5] + answers[6],
      'Conversion Path': answers[7] + answers[8],
      'Follow-Up': answers[9] + answers[10]
    };

    // Bottleneck logic: lowest score with priority rule
    const priority = ['Conversion Path', 'Offer Clarity', 'Visibility', 'Trust/Proof', 'Follow-Up'];
    let bottleneck = priority[0];
    let minScore = 21;

    priority.forEach(cat => {
      if (categories[cat] < minScore) {
        minScore = categories[cat];
        bottleneck = cat;
      }
    });

    return { totalScore, bottleneck };
  };

  const results = useMemo(() => step === 11 ? calculateResults() : null, [step, answers]);

  const handleAnswer = (score) => {
    setAnswers({ ...answers, [step]: score });
    if (step < 10) {
      setStep(step + 1);
    } else {
      setStep(11);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getScoreBand = (score) => {
    if (score <= 39) return {
      title: "Your Funnel is Leaking (But Fixable)",
      text: "Right now, local pet owners aren’t consistently seeing you—or they don’t know what to do next. The good news: small changes this week can create momentum fast."
    };
    if (score <= 69) return {
      title: "You’re Close—One Bottleneck is Holding You Back",
      text: "You have some strong pieces in place, but one weak link is blocking consistent inquiries. Fix the bottleneck below and you’ll feel the difference quickly."
    };
    if (score <= 84) return {
      title: "Strong Foundation—Ready to Improve Consistency",
      text: "Your marketing is working, but a few upgrades will increase inquiries and make results more predictable."
    };
    return {
      title: "Scale Ready",
      text: "You’ve built a strong foundation. With consistent content and the right ads, you’re positioned to scale your lead flow confidently."
    };
  };

  const getBottleneckContent = (bottleneck) => {
    const data = {
      'VISIBILITY': {
        heading: "Your #1 Bottleneck: Visibility",
        text: "You can be great at what you do—but if local pet owners don’t see you consistently, they won’t think of you when they need help.",
        steps: [
          "Post 3–5x/week using a simple routine (2 engagement, 2 educational, 1 promotional).",
          "Share each post to Stories to stay top of mind daily.",
          "Run Brand Awareness Ads to reach more local pet owners faster."
        ],
        nextMove: "Brand Awareness Ads"
      },
      'TRUST/PROOF': {
        heading: "Your #1 Bottleneck: Trust & Proof",
        text: "Pet owners book who they trust. If your page doesn’t show proof and reliability quickly, people hesitate—even if they like you.",
        steps: [
          "Add 3 proof posts: a testimonial, a behind-the-scenes moment, and a happy client/pet photo.",
          "Update your bio to clearly say what you do, where you serve, and how to book.",
          "Promote your best proof post with Brand Awareness Ads to build trust locally."
        ],
        nextMove: "Brand Awareness Ads"
      },
      'OFFER CLARITY': {
        heading: "Your #1 Bottleneck: Offer Clarity",
        text: "People can’t book what they don’t understand. If your services and next steps aren’t crystal clear, inquiries drop.",
        steps: [
          "Choose one core offer to lead with (ex: recurring walks, grooming package, overnights, daycare trial).",
          "Write 3 bullets: who it’s for, what’s included, and how to book.",
          "Once your offer is clear, run Lead Generation Ads (or start with awareness if you’re brand new)."
        ],
        nextMove: "Clarify Offer → Then Lead Gen Ads"
      },
      'CONVERSION PATH': {
        heading: "Your #1 Bottleneck: Conversion Path",
        text: "You may be getting attention—but if it’s not obvious how to take the next step, leads fall through.",
        steps: [
          "Add one clear CTA everywhere: ‘DM AVAILABILITY’ or ‘Request a Quote’.",
          "Use a simple form/link with only the basics (name, service, dates, location).",
          "Run Lead Generation Ads optimized for messages or form submissions."
        ],
        nextMove: "Lead Generation Ads"
      },
      'FOLLOW-UP': {
        heading: "Your #1 Bottleneck: Follow-Up",
        text: "Most leads don’t book on the first message. Fast replies and simple follow-ups are often the difference between ‘maybe’ and a booking.",
        steps: [
          "Create 3 saved replies: availability, pricing range, and next steps.",
          "Follow up once at 24 hours if they don’t respond.",
          "If lead volume increases, add a simple CRM/automation so inquiries don’t get missed."
        ],
        nextMove: "Fix Follow-Up → Then Scale Ads"
      }
    };
    return data[bottleneck.toUpperCase()];
  };

  return (
    <>
      <SEO 
        title="Pet Business Lead Funnel Score | Free Marketing Audit" 
        description="Find the #1 reason you’re not getting consistent inquiries in your pet business. 2-minute free audit for dog walkers, groomers, and pet pros." 
        path="/lead-funnel-score" 
      />

      {step === 0 && (
        <>
          {/* Landing State */}
          <section className="bg-brand-white pt-20 pb-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-brand-blush/20 text-brand-primaryDark px-4 py-2 rounded-full text-sm font-bold mb-8">
                <SafeIcon icon={FiBarChart2} /> Free Marketing Tool
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-brand-primaryDark mb-6 leading-tight">
                Get Your Pet Business Lead Funnel Score <span className="text-brand-mediumPurple">in 2 Minutes</span>
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                Find the #1 reason you’re not getting consistent inquiries—and what to fix first. Built specifically for local pet service businesses.
              </p>
              <div className="flex flex-col items-center gap-4">
                <button 
                  onClick={() => setStep(1)}
                  className="w-full sm:w-auto bg-brand-primaryDark hover:bg-[#1f2552] text-white px-10 py-5 rounded-2xl font-bold text-xl transition-all shadow-xl flex items-center justify-center gap-3 group"
                >
                  Start the Free 2-Minute Audit <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-sm font-bold text-gray-400">Free. No call required.</p>
              </div>
            </div>
          </section>

          {/* Value Cards */}
          <section className="py-24 bg-brand-blush/10 px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Your Score (0–100)", desc: "A clear snapshot of how well your marketing turns attention into inquiries.", icon: FiBarChart2 },
                { title: "Your #1 Bottleneck", desc: "We pinpoint the single biggest thing blocking leads right now.", icon: FiTarget },
                { title: "A Fix-This-Week Plan", desc: "Three simple steps you can implement immediately.", icon: FiZap }
              ].map((card, i) => (
                <div key={i} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center">
                  <div className="w-12 h-12 bg-brand-mediumPurple/10 rounded-xl flex items-center justify-center mx-auto mb-6 text-brand-mediumPurple">
                    <SafeIcon icon={card.icon} size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-brand-primaryDark mb-3">{card.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* How it Works */}
          <section className="py-24 bg-brand-white px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-brand-primaryDark mb-16">How It Works</h2>
              <div className="space-y-12">
                {[
                  "Answer 10 quick questions",
                  "Get your score + biggest bottleneck",
                  "Follow the 3-step plan for this week"
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-6">
                    <div className="w-12 h-12 bg-brand-primaryDark text-white rounded-full flex items-center justify-center font-black text-xl shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-xl font-medium text-brand-primaryDark">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-24 bg-brand-blush/10 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-brand-primaryDark mb-12">Common Questions</h2>
              <div className="space-y-4">
                {[
                  { q: "Is this really free?", a: "Yes—this is a free audit tool to help you understand what to fix first." },
                  { q: "Do I need anything prepared?", a: "No—just answer the questions based on what you’re doing right now." }
                ].map((faq, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                    <button 
                      onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                      className="w-full flex justify-between items-center p-6 text-left font-bold text-brand-primaryDark"
                    >
                      {faq.q}
                      <SafeIcon icon={FiPlus} className={`transition-transform ${activeFaq === i ? 'rotate-45' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeFaq === i && (
                        <motion.div initial={{height: 0}} animate={{height: 'auto'}} exit={{height: 0}}>
                          <div className="p-6 pt-0 text-gray-600 border-t border-gray-50">{faq.a}</div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {step >= 1 && step <= 10 && (
        <section className="min-h-[80vh] flex items-center justify-center px-4 py-20 bg-brand-blush/10">
          <div className="w-full max-w-2xl">
            <div className="mb-8">
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs font-black text-brand-mediumPurple uppercase tracking-widest">Step {step} of 10</span>
                <span className="text-xs font-bold text-gray-400">{Math.round((step/10)*100)}% Complete</span>
              </div>
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-brand-mediumPurple" 
                  initial={{ width: 0 }}
                  animate={{ width: `${(step/10)*100}%` }}
                />
              </div>
            </div>

            <motion.div 
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white p-8 md:p-12 rounded-[32px] shadow-2xl border border-gray-100"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-brand-primaryDark mb-8 leading-tight">
                {QUESTIONS[step - 1].q}
              </h2>
              <div className="space-y-4">
                {QUESTIONS[step - 1].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(opt.score)}
                    className="w-full text-left p-6 rounded-2xl border-2 border-gray-100 hover:border-brand-mediumPurple hover:bg-brand-mediumPurple/5 transition-all group flex justify-between items-center"
                  >
                    <span className="text-lg font-medium text-gray-700 group-hover:text-brand-primaryDark transition-colors">
                      {opt.text}
                    </span>
                    <SafeIcon icon={FiArrowRight} className="text-gray-300 group-hover:text-brand-mediumPurple transition-colors" />
                  </button>
                ))}
              </div>
              {step > 1 && (
                <button 
                  onClick={() => setStep(step - 1)}
                  className="mt-8 text-gray-400 text-sm font-bold flex items-center gap-2 hover:text-brand-primaryDark transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} /> Back
                </button>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {step === 11 && results && (
        <section className="py-20 px-4 bg-brand-blush/5">
          <div className="max-w-4xl mx-auto">
            {/* Results Summary Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-brand-primaryDark text-white rounded-[40px] p-8 md:p-12 shadow-2xl mb-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center items-center">
                <div>
                  <p className="text-brand-blush text-xs font-black uppercase tracking-widest mb-2">Your Score</p>
                  <p className="text-6xl md:text-7xl font-black text-white">{results.totalScore}<span className="text-2xl opacity-40">/100</span></p>
                </div>
                <div className="md:border-x border-white/10 md:px-8">
                  <p className="text-brand-blush text-xs font-black uppercase tracking-widest mb-2">#1 Bottleneck</p>
                  <p className="text-2xl md:text-3xl font-bold leading-tight">{results.bottleneck}</p>
                </div>
                <div>
                  <p className="text-brand-blush text-xs font-black uppercase tracking-widest mb-2">Best Next Move</p>
                  <p className="text-xl font-bold leading-tight">{getBottleneckContent(results.bottleneck).nextMove}</p>
                </div>
              </div>
              <div className="mt-10 pt-8 border-t border-white/10 text-center">
                <p className="text-white/60 text-sm italic">Simple fix first → consistent leads later.</p>
              </div>
            </motion.div>

            {/* Score Band Section */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 mb-12">
              <h2 className="text-3xl font-bold text-brand-primaryDark mb-4">{getScoreBand(results.totalScore).title}</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {getScoreBand(results.totalScore).text}
              </p>
            </div>

            {/* Bottleneck Deep Dive */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 mb-12 border-l-8 border-brand-mediumPurple">
              <div className="flex items-center gap-3 text-brand-mediumPurple mb-6">
                <SafeIcon icon={FiAlertCircle} size={32} />
                <h2 className="text-3xl font-bold text-brand-primaryDark">{getBottleneckContent(results.bottleneck).heading}</h2>
              </div>
              <p className="text-xl text-brand-primaryDark/80 mb-10 leading-relaxed italic">
                "{getBottleneckContent(results.bottleneck).text}"
              </p>
              
              <h3 className="text-xl font-bold text-brand-primaryDark mb-6">Fix This Week (3 Steps):</h3>
              <div className="space-y-6">
                {getBottleneckContent(results.bottleneck).steps.map((step, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="w-8 h-8 rounded-lg bg-brand-blush/20 text-brand-primaryDark flex items-center justify-center font-bold shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-lg text-gray-700 font-medium leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Soft Next Steps */}
            <div className="text-center bg-brand-mediumPurple rounded-[40px] p-12 text-white overflow-hidden relative">
              <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-4">Want a weekly plan you can use immediately?</h2>
                <p className="text-white/80 mb-10">Get the free tool I built to turn your bottleneck fix into actual posts.</p>
                
                <div className="flex flex-col items-center gap-6">
                  <div className="flex flex-col items-center">
                    <a 
                      href="https://chatgpt.com/g/g-69a66c0ee14081918a59af31ad9cef4c-pet-content-planner" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-brand-primaryDark text-white px-10 py-5 rounded-2xl font-bold text-xl hover:bg-[#1f2552] transition-all shadow-xl flex items-center gap-3"
                    >
                      Get My Free Weekly Content Plan <SafeIcon icon={FiExternalLink} />
                    </a>
                    <p className="text-[10px] uppercase tracking-widest text-white/50 mt-3">Opens in ChatGPT (free account required)</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-6 mt-4">
                    <Link to="/services/social-media-management" className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold transition-all border border-white/20">
                      See Packages
                    </Link>
                    <a href="https://tidycal.com/digital-by-perry/schedule-a-call" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white font-bold py-4">
                      Prefer to talk? <span className="underline">Book a call</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}